/**
 * Virtual scrolling logic for efficient rendering of large result sets.
 * Only renders visible rows based on viewport height and scroll position.
 */

export interface VirtualScrollConfig {
  totalRows: number
  rowHeight: number
  viewportHeight: number
  bufferRows: number // Extra rows to render above/below for smooth scroll
}

export interface VisibleRange {
  startIndex: number
  endIndex: number
  offsetY: number
}

export class VirtualScroller {
  private config: VirtualScrollConfig

  constructor(config: VirtualScrollConfig) {
    this.config = config
  }

  updateConfig(config: Partial<VirtualScrollConfig>): void {
    this.config = { ...this.config, ...config }
  }

  getVisibleRange(scrollTop: number): VisibleRange {
    const { totalRows, rowHeight, viewportHeight, bufferRows } = this.config

    // Calculate the first visible row
    const firstVisibleRow = Math.floor(scrollTop / rowHeight)

    // Calculate start index with buffer
    const startIndex = Math.max(0, firstVisibleRow - bufferRows)

    // Calculate how many rows fit in the viewport
    const visibleRowCount = Math.ceil(viewportHeight / rowHeight)

    // Calculate end index with buffer
    const endIndex = Math.min(
      totalRows,
      firstVisibleRow + visibleRowCount + bufferRows,
    )

    // Calculate the Y offset for positioning the first rendered row
    const offsetY = startIndex * rowHeight

    return {
      startIndex,
      endIndex,
      offsetY,
    }
  }

  getTotalHeight(): number {
    return this.config.totalRows * this.config.rowHeight
  }

  getRowHeight(): number {
    return this.config.rowHeight
  }

  getTotalRows(): number {
    return this.config.totalRows
  }
}
